import React, { useEffect, useState } from 'react'
import {
  CBadge,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilOptions, cilPencil, cilTrash } from '@coreui/icons'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TeamService from 'src/service/TeamService'
import Notify from '../Notify'
import CommonService from 'src/service/CommonService'
import FixturesModal from './FixturesModal'

const API_URL = process.env.REACT_APP_API_URL

const columns = [
  { label: 'Team Code', key: 'sn', _style: { width: '15%' } },
  { label: 'Team Logo', key: 'image', _style: { width: '15%' }, filter: false, sorter: false },
  { label: 'Team Name', key: 'team_name' },
  {
    label: 'Fixtures',
    key: 'fixture_count',
    _style: { width: '10%' },
    filter: false,
    sorter: false,
  },
  { label: 'Comp Name', key: 'comp_name' },
  { key: 'actions', label: 'Actions', filter: false, sorter: false },
]

const Table = ({
  users,
  fetchUsers,
  setActivePage,
  setItemsPerPage,
  setColumnFilter,
  setColumnSorter,
  itemsPerPage,
  activePage,
  columnFilter,
  columnSorter,
  onEditTeam,
}) => {
  const [loading, setLoading] = useState(false)
  const [fixturesTeam, setFixturesTeam] = useState(null)

  const deleteTeam = (id) => {
    setLoading(true)
    TeamService.deleteTeam({ id })
      .then((res) => {
        if (res.status === 200) {
          fetchUsers()
          ToastComponent(res.message, 'success')
        } else {
          ToastComponent('Failed to delete team', 'error')
        }
      })
      .catch(() => {
        ToastComponent('An error occurred', 'error')
      })
      .finally(() => setLoading(false))
  }

  // Are you sure want modal
  const [handleYes, setHandleYes] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [handleNo, setHandleNo] = useState(false)
  const [tableId, setTableId] = useState(false)

  const handleCancel = () => setShowConfirm(false)

  const handleConfirm = () => {
    deleteTeam(tableId)
    setShowConfirm(false)
  }

  const areYouSureModal = (id) => {
    const data = { id, type: 'team' }
    CommonService.checkItemExists(data).then((res) => {
      if (res.status === 200) {
        if (res.data) {
          ToastComponent(res.message, 'error')
        } else {
          setShowConfirm(true)
          setTableId(id)
        }
      }
    })
  }

  useEffect(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    fetchUsers(offset, itemsPerPage, activePage, columnFilter, columnSorter)
  }, [activePage, columnFilter, columnSorter, itemsPerPage, fetchUsers])

  return (
    <div className="ge-datatable ge-datatable--static">
      <CSmartTable
        columns={columns}
        columnFilter={{ external: true }}
        columnSorter={{ external: true }}
        scopedColumns={{
          image: (item) => (
            <td>
              {item.image ? (
                <img
                  src={`${API_URL}uploads/team_sponsor/${item.image}`}
                  alt="team_sponsor"
                  width="40"
                  height="40"
                  className="rounded"
                />
              ) : (
                <img
                  src={`${API_URL}uploads/game_logo/${item.game_logo}`}
                  alt="game_logo"
                  width="40"
                  height="40"
                  className="rounded"
                />
              )}
            </td>
          ),
          team_name: (item) => (
            <td>
              <span
                className="fw-semibold text-primary"
                role="button"
                onClick={() => onEditTeam(item.id)}
              >
                {item.team_name}
              </span>
            </td>
          ),
          fixture_count: (item) => {
            const count = Number(item.fixture_count) || 0
            return (
              <td>
                {count > 0 ? (
                  <CBadge
                    color="primary"
                    shape="rounded-pill"
                    role="button"
                    title="View fixtures"
                    onClick={() => setFixturesTeam(item)}
                  >
                    {count}
                  </CBadge>
                ) : (
                  <CBadge color="secondary" shape="rounded-pill">
                    0
                  </CBadge>
                )}
              </td>
            )
          },
          actions: (item) => (
            <td className="py-2">
              <CDropdown alignment="end" placement="bottom-end" portal>
                <CDropdownToggle color="transparent" caret={false} className="p-0">
                  <CIcon icon={cilOptions} role="button" />
                </CDropdownToggle>
                <CDropdownMenu className="ge-row-actions-menu">
                  <CDropdownItem role="button" onClick={() => onEditTeam(item.id)}>
                    <CIcon icon={cilPencil} className="me-2" />
                    Edit
                  </CDropdownItem>
                  <CDropdownItem
                    role="button"
                    className="text-danger"
                    onClick={() => areYouSureModal(item.id)}
                  >
                    <CIcon icon={cilTrash} className="me-2" />
                    Delete
                  </CDropdownItem>
                </CDropdownMenu>
              </CDropdown>
            </td>
          ),
        }}
        items={users?.data}
        itemsPerPage={itemsPerPage}
        itemsPerPageSelect
        loading={loading}
        pagination={{ external: true }}
        paginationProps={{
          activePage: activePage,
          pages: Math.ceil(users?.total / itemsPerPage) || 1,
        }}
        tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
        onActivePageChange={(activePage) => setActivePage(activePage)}
        onColumnFilterChange={(filter) => {
          setActivePage(1)
          setColumnFilter(filter)
        }}
        onItemsPerPageChange={(itemsPerPage) => {
          setActivePage(1)
          setItemsPerPage(itemsPerPage)
        }}
        onSorterChange={(sorter) => setColumnSorter(sorter)}
      />
      <Notify
        setShowConfirm={setShowConfirm}
        showConfirm={showConfirm}
        setHandleNo={setHandleNo}
        handleNo={handleNo}
        handleYes={handleYes}
        setHandleYes={setHandleYes}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        text="Are you sure you want to delete this?"
      />
      <FixturesModal
        team={fixturesTeam}
        visible={!!fixturesTeam}
        onClose={() => setFixturesTeam(null)}
      />
    </div>
  )
}

export default Table
